import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate, useBlocker } from 'react-router-dom';
import { tableService } from '../services/table';
import { getActiveRestaurantId } from '../utils/roleAccess';
import { useRestaurantWebSocket } from '../hooks/useRestaurantWebSocket';
import { itemService } from '../services/item';
import { orderService } from '../services/order';
import { printService, willUseShare } from '../services/print';
import { PrintShareToggle } from '../components/PrintShareToggle';
import { orgSettingsService } from '../services/orgSettings';
import { WhatsAppShareModal } from '../components/WhatsAppShareModal';
import { kitchenService } from '../services/kitchen';
import { itemCategoryService } from '../services/itemCategory';
import { customerService } from '../services/customer';
import { printConfigurationService } from '../services/printConfiguration';
import { Table, Item, OrderItem, Order, PaymentMode, Kitchen } from '../types';
import {
  ShoppingCart,
  Users,
  Plus,
  Minus,
  X,
  Check,
  Search,
  Edit3,
  Clock3,
  AlertCircle,
  Trash2,
  ChefHat,
  Printer,
  CheckCircle,
  Banknote,
  CreditCard,
  Calendar,
  GitMerge,
  Share2,
  Percent,
  IndianRupee,
  Phone
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import { InlineLoader, ButtonLoader, SkeletonCard } from '../components/LoadingStates';
import VegIcon from '../assets/Veg.png';
import NonVegIcon from '../assets/Non_veg.png';

interface SelectedItem extends OrderItem {
  lineKey: string;       // unique per (item + variant + modifier set)
  itemNotes?: string;
  isNewItem?: boolean;
  kitchenId?: string;
  itemType?: string;
  vegType?: string;
  item?: Item; // kept if you ever need the full item
}

const hasOptions = (item: Item) => !!((item.variants && item.variants.length) || (item.modifierGroups && item.modifierGroups.length));

const makeLineKey = (itemId: string, variantName?: string, modifiers?: { groupName: string; optionName: string }[]) =>
  [itemId, variantName || '', (modifiers || []).map(m => `${m.groupName}:${m.optionName}`).sort().join(',')].join('|');

interface TableWithOrder extends Table {
  currentOrders?: Order[];
}

interface CustomerDetails {
  customerId?: string;
  customerName: string;
  customerMobile: string;
  customerEmail?: string;
  customerAddress?: string;
}

export const PlaceOrder: React.FC = () => {
  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showTableModal, setShowTableModal] = useState(false);
  const [showOrderSummaryModal, setShowOrderSummaryModal] = useState(false);
  const [selectedTable, setSelectedTable] = useState<TableWithOrder | null>(null);
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
  const [customizing, setCustomizing] = useState<Item | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedVegType, setSelectedVegType] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [orderNotes, setOrderNotes] = useState('');
  const [showOrderNotes, setShowOrderNotes] = useState(false);
  const [paymentMode] = useState<PaymentMode>('CASH');
  const [discountPercent] = useState<number>(0);
  const [discountAmount] = useState<number>(0);
  const [customerDetails] = useState<CustomerDetails>({
    customerName: '',
    customerMobile: '',
    customerEmail: '',
    customerAddress: ''
  });

  // NEW: edit/update flags + existing order reference + confirm modal
  const [isEditingExisting, setIsEditingExisting] = useState(false);
  const [existingOrder, setExistingOrder] = useState<Order | null>(null);
  const [showExistingOrderChoice, setShowExistingOrderChoice] = useState(false);
  const [activeImageIdx, setActiveImageIdx] = useState<Record<string, number>>({});
  const hoveredItemIdRef = useRef<string | null>(null);

  // ── Order management (settle / merge / print) ──────────────────────────────
  const [showSettleModal, setShowSettleModal] = useState(false);
  const [settleTargetOrder, setSettleTargetOrder] = useState<Order | null>(null);
  const [mergeSelectedIds, setMergeSelectedIds] = useState<string[]>([]);
  const [settlementData, setSettlementData] = useState({
    paymentMode: 'CASH' as PaymentMode,
    customerId: '',
    customerName: '',
    customerMobile: '',
    customerEmail: '',
    customerAddress: '',
    discountPercent: null as number | null,
    discountAmount: null as number | null,
  });
  const [discountType, setDiscountType] = useState<'percent' | 'amount' | null>(null);
  const [settleRoundOff, setSettleRoundOff] = useState(true);
  const [settleCustomerSearchTerm, setSettleCustomerSearchTerm] = useState('');
  const [debouncedSettleCustomer, setDebouncedSettleCustomer] = useState('');
  const [showSettleCustomerDropdown, setShowSettleCustomerDropdown] = useState(false);
  const [selectedCustomerForSettle, setSelectedCustomerForSettle] = useState<any>(null);
  const [settleMobileError, setSettleMobileError] = useState('');
  const [settleEmailError, setSettleEmailError] = useState('');
  const [printingKOT, setPrintingKOT] = useState(false);
  // "Print / Share" toggles next to Place Order & Settle (unchecked by default on web)
  const [placePrintShare, setPlacePrintShare] = useState(false);
  const [settlePrintShare, setSettlePrintShare] = useState(false);
  const [waShare, setWaShare] = useState<{ orderId: string; mobile?: string; name?: string } | null>(null);

  const activeRestaurantId = getActiveRestaurantId();

  // Org-level WhatsApp auto-send flag — decides whether to prompt after settle
  const { data: orgEmailSettings } = useQuery(
    ['orgEmailSettings', user?.organizationCode],
    () => orgSettingsService.getEmailSettings(),
    { enabled: !!user?.organizationCode, staleTime: 60_000 }
  );

  const promptWhatsAppShare = (settled: Order) => {
    if (!orgEmailSettings?.whatsappEnabled) return; // WhatsApp not enabled for this org
    if (orgEmailSettings?.whatsappAutoSend) return; // sent automatically by the backend
    setWaShare({
      orderId: settled.id,
      mobile: settlementData.customerMobile,
      name: settlementData.customerName,
    });
  };

  // WebSocket — live table status updates
  useRestaurantWebSocket({
    onTableUpdate: useCallback(() => {
      queryClient.invalidateQueries(['tables', user?.organizationCode, activeRestaurantId]);
      queryClient.invalidateQueries(['openOrders', user?.organizationCode, activeRestaurantId]);
    }, [queryClient, user?.organizationCode, activeRestaurantId]),
  });

  // ── Unplaced-order navigation guard ────────────────────────────────────────
  // While a table is selected the order hasn't been placed yet — block leaving
  // the page (sidebar links, browser back) and ask to cancel or stay.
  const resetOrderSession = useCallback(() => {
    setSelectedTable(null);
    setSelectedItems([]);
    setIsEditingExisting(false);
    setExistingOrder(null);
    setOrderNotes('');
    setShowExistingOrderChoice(false);
  }, []);

  const navigationBlocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }: any) =>
        !!selectedTable && currentLocation.pathname !== nextLocation.pathname,
      [selectedTable]
    )
  );

  // If the selection got cleared while a navigation was pending, just let it through
  useEffect(() => {
    if (navigationBlocker.state === 'blocked' && !selectedTable) {
      navigationBlocker.proceed();
    }
  }, [navigationBlocker, selectedTable]);

  // Also warn on refresh / tab close while an order is in progress
  useEffect(() => {
    if (!selectedTable) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [selectedTable]);

  // Fetch tables filtered by active restaurant when available
  const { data: allTables, isLoading: tablesLoading } = useQuery(
    ['tables', user?.organizationCode, activeRestaurantId],
    () => tableService.getTables({
      org: user?.organizationCode!,
      ...(activeRestaurantId && { restaurantId: activeRestaurantId }),
    }),
    { enabled: !!user?.organizationCode }
  );

  // Fetch open orders (NOTE: your backend returns Page<Order>, so normalize)
  const { data: openOrders } = useQuery(
    ['openOrders', user?.organizationCode, activeRestaurantId],
    () => orderService.getOpenOrders(user?.organizationCode!, activeRestaurantId),
    { enabled: !!user?.organizationCode }
  );

  // Fetch all kitchens
  const { data: kitchens } = useQuery(
    ['kitchens', user?.organizationCode],
    () => kitchenService.getKitchens(user?.organizationCode!),
    { enabled: !!user?.organizationCode }
  );

  // Fetch item categories
  const { data: categories } = useQuery(
    ['itemCategories', user?.organizationCode],
    () => itemCategoryService.getActiveCategories(user?.organizationCode!),
    { enabled: !!user?.organizationCode }
  );

  // normalize open orders to a simple array
  type PageLike<T> = { content?: T[] } & Record<string, unknown>;

  const openOrdersList: Order[] = useMemo(() => {
    const raw = openOrders as PageLike<Order> | Order[] | undefined;
    if (Array.isArray(raw)) return raw;
    if (raw && Array.isArray(raw.content)) return raw.content;
    return [];
  }, [openOrders]);

  // Fetch all items or search based on term
  const { data: items, isLoading: itemsLoading } = useQuery(
    ['items', user?.organizationCode, debouncedSearchTerm, selectedVegType, selectedCategory],
    async () => {
      if (debouncedSearchTerm.trim()) {
        return itemService.searchItems(user?.organizationCode!, debouncedSearchTerm.trim());
      } else {
        // Fetch items with filters
        return itemService.getItems({
          org: user?.organizationCode!,
          ...(selectedVegType && { vegType: selectedVegType }),
          ...(selectedCategory && { categoryId: selectedCategory })
        });
      }
    },
    {
      enabled: !!user?.organizationCode && !!selectedTable,
      keepPreviousData: true
    }
  );

  // Create kitchen map for quick lookup
  const kitchenMap = useMemo(() => {
    if (!kitchens) return {} as Record<string, Kitchen>;
    return kitchens.reduce((acc, kitchen) => {
      acc[kitchen.id] = kitchen;
      return acc;
    }, {} as Record<string, Kitchen>);
  }, [kitchens]);

  // Combine tables with their current orders (using normalized list)
  const tables: TableWithOrder[] = useMemo(() => {
    if (!allTables) return [];
    return allTables.map(table => {
      const currentOrders = openOrdersList.filter(
        o => String(o.tableId) === String(table.id)
      );
      return { ...table, currentOrders };
    });
  }, [allTables, openOrdersList]);


  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Auto-scroll images every 3 s; pause when hovering
  useEffect(() => {
    const hasMulti = items?.some(i => (i.images?.length ?? 0) > 1);
    if (!hasMulti) return;
    const id = setInterval(() => {
      setActiveImageIdx(prev => {
        if (!items) return prev;
        const next = { ...prev };
        items.forEach(item => {
          if ((item.images?.length ?? 0) > 1 && hoveredItemIdRef.current !== item.id) {
            const curr = prev[item.id] ?? 0;
            next[item.id] = (curr + 1) % item.images!.length;
          }
        });
        return next;
      });
    }, 3000);
    return () => clearInterval(id);
  }, [items]);

  // Debounce settle customer search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSettleCustomer(settleCustomerSearchTerm), 400);
    return () => clearTimeout(t);
  }, [settleCustomerSearchTerm]);

  // Customer search for settle form
  const { data: settleCustomerResults } = useQuery(
    ['settle-customer-search', user?.organizationCode, debouncedSettleCustomer],
    () => customerService.search(user?.organizationCode!, debouncedSettleCustomer.trim(), 10),
    { enabled: !!user?.organizationCode && debouncedSettleCustomer.trim().length >= 2 }
  );

  // Print config (for GST display in settle form)
  const { data: printConfigs } = useQuery(
    ['printConfigs', user?.organizationCode, activeRestaurantId],
    () => printConfigurationService.getAll(user?.organizationCode!, activeRestaurantId),
    { enabled: !!user?.organizationCode }
  );
  const isGstApplicable = printConfigs?.find((c: any) => c.printType === 'BILL')?.gstApplicable !== false;

  // Place order mutation
  const placeOrderMutation = useMutation(
    async () => {
      if (!activeRestaurantId) {
        throw new Error('Please select a restaurant before placing an order');
      }

      if (!selectedTable || selectedItems.length === 0) {
        throw new Error('Please select a table and add items');
      }

      if (!selectedTable.active) {
        throw new Error('Cannot place order on an inactive table');
      }

      const orderData = {
        organizationCode: user?.organizationCode!,
        tableId: selectedTable.id,
        paymentMode,
        notes: orderNotes || undefined,
        discountPercent: discountPercent > 0 ? discountPercent : undefined,
        discountAmount: discountAmount > 0 ? discountAmount : undefined,
        customerId: customerDetails.customerId || undefined,
        customerName: customerDetails.customerName || undefined,
        customerMobile: customerDetails.customerMobile || undefined,
        customerEmail: customerDetails.customerEmail || undefined,
        customerAddress: customerDetails.customerAddress || undefined,
        ...(activeRestaurantId && { restaurantId: activeRestaurantId }),
        items: selectedItems.map(item => ({
          itemId: item.itemId!,
          quantity: item.quantity,
          specialNotes: item.itemNotes || undefined,
          variantName: item.variantName || undefined,
          modifiers: (item.modifiers || []).map(m => ({ groupName: m.groupName, optionName: m.optionName })),
        }))
      };

      return orderService.placeOrder(orderData);
    },
    {
      onSuccess: () => {
        toast.success('Order placed successfully!');
        queryClient.invalidateQueries(['orders']);
        queryClient.invalidateQueries(['openOrders', user?.organizationCode]);
        queryClient.invalidateQueries(['tables', user?.organizationCode]);
        setSelectedTable(null);
        setSelectedItems([]);
        setOrderNotes('');
        setIsEditingExisting(false);
        setExistingOrder(null);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to place order');
      }
    }
  );

  // Place order and print KOT mutation
  const placeOrderAndPrintMutation = useMutation(
    async () => {
      if (!activeRestaurantId) {
        throw new Error('Please select a restaurant before placing an order');
      }

      if (!selectedTable || selectedItems.length === 0) {
        throw new Error('Please select a table and add items');
      }

      if (!selectedTable.active) {
        throw new Error('Cannot place order on an inactive table');
      }

      const orderData = {
        organizationCode: user?.organizationCode!,
        tableId: selectedTable.id,
        paymentMode,
        notes: orderNotes || undefined,
        discountPercent: discountPercent > 0 ? discountPercent : undefined,
        discountAmount: discountAmount > 0 ? discountAmount : undefined,
        customerId: customerDetails.customerId || undefined,
        customerName: customerDetails.customerName || undefined,
        customerMobile: customerDetails.customerMobile || undefined,
        customerEmail: customerDetails.customerEmail || undefined,
        customerAddress: customerDetails.customerAddress || undefined,
        ...(activeRestaurantId && { restaurantId: activeRestaurantId }),
        items: selectedItems.map(item => ({
          itemId: item.itemId!,
          quantity: item.quantity,
          specialNotes: item.itemNotes || undefined,
          variantName: item.variantName || undefined,
          modifiers: (item.modifiers || []).map(m => ({ groupName: m.groupName, optionName: m.optionName })),
        }))
      };

      return orderService.placeOrder(orderData);
    },
    {
      onSuccess: async (order: any) => {
        toast.success('Order placed successfully!');

        // Print/Share KOT
        try {
          await printService.printKOT(order.orderId, user?.organizationCode!, true);
          toast.success(willUseShare() ? 'KOT shared successfully!' : 'KOT sent to print!');
        } catch (error) {
          console.error('Failed to print/share KOT:', error);
          toast.error(willUseShare() ? 'Order placed but sharing failed' : 'Order placed but printing failed');
        }

        queryClient.invalidateQueries(['orders']);
        queryClient.invalidateQueries(['openOrders', user?.organizationCode]);
        queryClient.invalidateQueries(['tables', user?.organizationCode]);
        setSelectedTable(null);
        setSelectedItems([]);
        setOrderNotes('');
        setIsEditingExisting(false);
        setExistingOrder(null);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to place order');
      }
    }
  );

  // NEW: Update order mutation
  const updateOrderMutation = useMutation(
    async () => {
      if (!existingOrder) throw new Error('No existing order to update');

      const updateData = {
        notes: orderNotes || undefined,
        // if you add discount controls on this screen later, map them below:
        // discountPercent: discountPercent > 0 ? discountPercent : undefined,
        // discountAmount: discountAmount > 0 ? discountAmount : undefined,
        items: selectedItems.map(it => ({
          itemId: it.itemId,
          quantity: it.quantity,
          specialNotes: it.itemNotes || undefined,
          variantName: it.variantName || undefined,
          modifiers: (it.modifiers || []).map(m => ({ groupName: m.groupName, optionName: m.optionName })),
        }))
      };

      return orderService.updateOrder(existingOrder.id, updateData);
    },
    {
      onSuccess: () => {
        toast.success('Order updated successfully!');
        queryClient.invalidateQueries(['orders']);
        queryClient.invalidateQueries(['openOrders', user?.organizationCode]);
        queryClient.invalidateQueries(['tables', user?.organizationCode]);
        setSelectedTable(null);
        setSelectedItems([]);
        setOrderNotes('');
        setIsEditingExisting(false);
        setExistingOrder(null);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to update order');
      }
    }
  );

  // Settle mutation
  const settleOrderMutation = useMutation(
    (payload: any) => orderService.settleOrder(payload),
    {
      onSuccess: (settled: Order) => {
        promptWhatsAppShare(settled);
        queryClient.invalidateQueries(['orders']);
        queryClient.invalidateQueries(['openOrders', user?.organizationCode]);
        queryClient.invalidateQueries(['tables', user?.organizationCode]);
        toast.success('Order settled!');
        setShowSettleModal(false);
        setSettleTargetOrder(null);
        setDiscountType(null);
        setSettlementData({ paymentMode: 'CASH', customerId: '', customerName: '', customerMobile: '', customerEmail: '', customerAddress: '', discountPercent: null, discountAmount: null });
        setSelectedTable(null);
        setSelectedItems([]);
        setIsEditingExisting(false);
        setExistingOrder(null);
      },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to settle order'); },
    }
  );

  // Settle + print bill mutation
  const settleAndPrintMutation = useMutation(
    (payload: any) => orderService.settleOrder(payload),
    {
      onSuccess: async (order: Order) => {
        promptWhatsAppShare(order);
        toast.success('Order settled!');
        try {
          await printService.printBill(order.id, user?.organizationCode!, true);
          toast.success(willUseShare() ? 'Bill shared!' : 'Bill sent to print!');
        } catch {
          toast.error('Settled but bill print failed');
        }
        queryClient.invalidateQueries(['orders']);
        queryClient.invalidateQueries(['openOrders', user?.organizationCode]);
        queryClient.invalidateQueries(['tables', user?.organizationCode]);
        setShowSettleModal(false);
        setSettleTargetOrder(null);
        setDiscountType(null);
        setSettlementData({ paymentMode: 'CASH', customerId: '', customerName: '', customerMobile: '', customerEmail: '', customerAddress: '', discountPercent: null, discountAmount: null });
        setSelectedTable(null);
        setSelectedItems([]);
        setIsEditingExisting(false);
        setExistingOrder(null);
      },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to settle order'); },
    }
  );

  // Merge orders mutation
  const mergeOrdersMutation = useMutation(
    ({ masterOrderId, sourceOrderIds }: { masterOrderId: string; sourceOrderIds: string[] }) =>
      orderService.mergeOrders(masterOrderId, sourceOrderIds),
    {
      onSuccess: (merged: Order) => {
        setSettleTargetOrder(merged);
        setMergeSelectedIds([]);
        queryClient.invalidateQueries(['openOrders', user?.organizationCode]);
        queryClient.invalidateQueries(['tables', user?.organizationCode]);
        toast.success('Orders merged!');
      },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to merge orders'); },
    }
  );

  // Standalone merge mutation (not inside settle modal)
  const standaloneMergeMutation = useMutation(
    ({ masterOrderId, sourceOrderIds }: { masterOrderId: string; sourceOrderIds: string[] }) =>
      orderService.mergeOrders(masterOrderId, sourceOrderIds),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['openOrders', user?.organizationCode]);
        queryClient.invalidateQueries(['tables', user?.organizationCode]);
        toast.success('Orders merged successfully!');
      },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to merge orders'); },
    }
  );

  const confirmMergeOrders = (orders: Order[]) => {
    const master = orders[0];
    const sourceIds = orders.slice(1).map(o => o.id);
    const names = orders.map(o => `#${o.orderNo}`).join(', ');
    if (window.confirm(`Merge ${orders.length} orders (${names}) into Order #${master.orderNo}?\n\nAll items will be combined into the first order.`)) {
      standaloneMergeMutation.mutate({ masterOrderId: master.id, sourceOrderIds: sourceIds });
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  const validateMobile = (v: string) => (!v || /^[6-9]\d{9}$/.test(v)) ? '' : 'Enter a valid 10-digit mobile number';
  const validateEmail  = (v: string) => (!v.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) ? '' : 'Enter a valid email address';

  const handleSettleMobileChange = (value: string) => {
    const num = value.replace(/\D/g, '').slice(0, 10);
    setSettlementData(p => ({ ...p, customerMobile: num }));
    setSettleMobileError(validateMobile(num));
    setSettleCustomerSearchTerm(num);
    if (num.length >= 2) setShowSettleCustomerDropdown(true);
    else { setShowSettleCustomerDropdown(false); setSelectedCustomerForSettle(null); }
  };

  const handleClearSettleCustomerSelection = () => {
    setSelectedCustomerForSettle(null);
    setSettlementData(p => ({ ...p, customerId: '', customerName: '', customerEmail: '', customerAddress: '' }));
    setSettleCustomerSearchTerm('');
    setShowSettleCustomerDropdown(false);
  };

  const handleSettleCustomerSelect = (c: any) => {
    setSelectedCustomerForSettle(c);
    setSettlementData(p => ({ ...p, customerId: c.id, customerName: c.name, customerMobile: c.mobile, customerEmail: c.email || '', customerAddress: c.address || '' }));
    setSettleCustomerSearchTerm(c.mobile);
    setShowSettleCustomerDropdown(false);
    setSettleMobileError('');
  };

  const handlePrintKOT = async (order: Order) => {
    setPrintingKOT(true);
    try {
      await printService.printKOT(order.id, user?.organizationCode!, true);
      toast.success(willUseShare() ? 'KOT shared!' : 'KOT sent to print!');
    } catch { toast.error('Failed to print KOT'); }
    finally { setPrintingKOT(false); }
  };

  // Discount fields for the settle payload, folding round-off (drop paise → whole ₹).
  const buildSettleDiscountFields = () => {
    const base = settleTargetOrder?.total ?? 0;
    const dp = settlementData.discountPercent || 0;
    const da = settlementData.discountAmount || 0;
    const discountVal = dp > 0 ? (base * dp) / 100 : (da > 0 ? da : 0);
    if (settleRoundOff) {
      const payable = Math.floor(Math.max(0, base - discountVal));
      const flat = Math.round((base - payable) * 100) / 100;
      return { discountPercent: undefined, discountAmount: flat > 0 ? flat : undefined };
    }
    return {
      discountPercent: dp > 0 ? dp : undefined,
      discountAmount: da > 0 ? da : undefined,
    };
  };

  const confirmSettlement = (andPrint = false) => {
    if (!settleTargetOrder) return;
    const mobileErr = validateMobile(settlementData.customerMobile);
    if (mobileErr) { toast.error(mobileErr); return; }
    const emailErr = validateEmail(settlementData.customerEmail);
    if (emailErr) { setSettleEmailError(emailErr); return; }
    if (settlementData.paymentMode === 'DUE') {
      if (!settlementData.customerMobile.trim()) { toast.error('Mobile required for DUE'); return; }
      if (!settlementData.customerName.trim()) { toast.error('Name required for DUE'); return; }
    }
    const payload: any = {
      orderId: settleTargetOrder.id,
      paymentMode: settlementData.paymentMode,
      customerName: settlementData.customerName || undefined,
      customerMobile: settlementData.customerMobile || undefined,
      customerEmail: settlementData.customerEmail || undefined,
      customerAddress: settlementData.customerAddress || undefined,
      customerId: settlementData.customerId || undefined,
      ...buildSettleDiscountFields(),
    };
    const clean = Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== undefined && v !== ''));
    if (andPrint) settleAndPrintMutation.mutate(clean);
    else settleOrderMutation.mutate(clean);
  };

  const handleTableSelect = (table: TableWithOrder) => {
    if (!table.active) {
      return;
    }

    const hasOrders = (table.currentOrders?.length ?? 0) > 0;
    setSelectedTable(table);
    if (hasOrders) {
      setShowExistingOrderChoice(true);
    } else {
      setIsEditingExisting(false);
      setExistingOrder(null);
      setSelectedItems([]);
      setOrderNotes('');
      setShowTableModal(false);
    }
  };
  const handleChangeTable = () => {
    setShowTableModal(true);
  };

  // Load existing order items into the right pane for editing
  const loadExistingOrderForEdit = (order: Order) => {
    setIsEditingExisting(true);
    setExistingOrder(order);

    const mapped: SelectedItem[] = order.items.map((it: any) => ({
      lineKey: makeLineKey(it.itemId, it.variantName, it.modifiers),
      itemId: it.itemId,
      name: it.name,
      quantity: it.quantity,
      unitPrice: it.unitPrice,
      basePrice: it.basePrice,
      variantName: it.variantName,
      modifiers: it.modifiers || [],
      specialNotes: it.specialNotes,
      itemNotes: it.specialNotes
    }));

    setSelectedItems(mapped);
    setOrderNotes(order.notes ?? '');
    setShowExistingOrderChoice(false);
    setShowTableModal(false);
  };

  // Add a concrete cart line (item + chosen variant/modifiers + resolved unit price)
  const addLine = (
    item: Item,
    variantName: string | undefined,
    modifiers: { groupName: string; optionName: string; price: number }[],
    unitPrice: number,
  ) => {
    const lineKey = makeLineKey(item.id, variantName, modifiers);
    setSelectedItems(prev => {
      const existing = prev.find(si => si.lineKey === lineKey);
      if (existing) {
        return prev.map(si => si.lineKey === lineKey ? { ...si, quantity: si.quantity + 1 } : si);
      }
      return [...prev, {
        lineKey,
        itemId: item.id,
        name: item.name,
        quantity: 1,
        unitPrice,
        basePrice: item.price,
        variantName,
        modifiers,
        specialNotes: '',
        itemNotes: '',
        item,
        isNewItem: false,
      }];
    });
  };

  const addItem = (item: Item) => {
    // Items with variants/add-ons need a customization step
    if (hasOptions(item)) { setCustomizing(item); return; }
    addLine(item, undefined, [], item.price);
  };

  const updateQuantity = (lineKey: string, quantity: number) => {
    if (quantity <= 0) {
      setSelectedItems(prev => prev.filter(si => si.lineKey !== lineKey));
      return;
    }
    setSelectedItems(prev => prev.map(si => si.lineKey === lineKey ? { ...si, quantity } : si));
  };

  const updateItemNotes = (lineKey: string, notes: string) => {
    setSelectedItems(prev => prev.map(si => si.lineKey === lineKey ? { ...si, itemNotes: notes } : si));
  };

  const getImgIdx = (id: string) => activeImageIdx[id] ?? 0;
  const shiftImg = (e: React.MouseEvent, id: string, len: number, dir: 1 | -1) => {
    e.stopPropagation();
    setActiveImageIdx(prev => ({ ...prev, [id]: ((prev[id] ?? 0) + dir + len) % len }));
  };

  const getTableStatusDisplay = (table: TableWithOrder) => {
    const hasOrders = (table.currentOrders?.length ?? 0) > 0;
    if (hasOrders) {
      return { text: 'Occupied', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: AlertCircle };
    }
    // the rest unchanged…
    switch (table.status) {
      case 'AVAILABLE': return { text: 'Available', color: 'bg-green-100 text-green-800 border-green-200', icon: Clock3 };
      case 'IN_USE': return { text: 'In Use', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: AlertCircle };
      case 'RESERVED': return { text: 'Reserved', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: AlertCircle };
      case 'OUT_OF_SERVICE': return { text: 'Out of Service', color: 'bg-red-100 text-red-800 border-red-200', icon: AlertCircle };
      default: return { text: 'Available', color: 'bg-green-100 text-green-800 border-green-200', icon: Clock3 };
    }
  };


  // Calculate totals
  const subtotal = selectedItems.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
  const discountAmountCalculated = discountPercent > 0 ? (subtotal * discountPercent / 100) : discountAmount;
  const totalAmount = subtotal - discountAmountCalculated;

  if (tablesLoading) {
    return (
      <div className="min-h-screen p-3 sm:p-4 md:p-6" style={{ backgroundColor: currentTheme.colors.background.primary }}>
        <div className="space-y-4 sm:space-y-6">
          {/* Header Skeleton */}
          <SkeletonCard />

          {/* Tables Grid Skeleton */}
          <div>
            <div className="mb-4">
              <SkeletonCard />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3 md:gap-4">
              {[...Array(12)].map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          </div>

          {/* Items Section Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="lg:col-span-2">
              <SkeletonCard />
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                {[...Array(6)].map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            </div>
            <div>
              <SkeletonCard />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const primaryActionLoading = isEditingExisting
    ? updateOrderMutation.isLoading
    : (placeOrderMutation.isLoading || placeOrderAndPrintMutation.isLoading);
  const primaryActionDisabled = primaryActionLoading || !selectedTable || selectedItems.length === 0;

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="p-3 sm:p-4 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div
              className="grid place-items-center h-12 w-12 rounded-2xl text-white shrink-0"
              style={{
                background: `linear-gradient(135deg, ${currentTheme.colors.primary[500]}, ${currentTheme.colors.primary[700]})`,
                boxShadow: `0 14px 32px -12px ${currentTheme.colors.primary[500]}`,
              }}
            >
              <ShoppingCart className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight" style={{ color: currentTheme.colors.text.primary }}>
                Place Order
              </h1>
              <p className="text-sm mt-0.5" style={{ color: currentTheme.colors.text.secondary }}>
                {selectedTable ? `Ordering for ${selectedTable.name}` : 'Select a table to start ordering'}
              </p>
            </div>
          </div>
          {selectedTable && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
              <div
                className="flex items-center gap-2 px-4 py-2.5 rounded-2xl"
                style={{
                  backgroundColor: currentTheme.colors.primary[50],
                  border: `1px solid ${currentTheme.colors.primary[100]}`,
                }}
              >
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: currentTheme.colors.primary[500] }} />
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: currentTheme.colors.text.secondary }}>
                  Table
                </span>
                <span className="font-bold text-sm" style={{ color: currentTheme.colors.primary[700] }}>
                  {selectedTable.name}
                </span>
              </div>
              <button
                onClick={handleChangeTable}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-white rounded-2xl transition-transform hover:scale-[1.03]"
                style={{
                  background: `linear-gradient(135deg, ${currentTheme.colors.primary[500]}, ${currentTheme.colors.primary[700]})`,
                  boxShadow: `0 12px 26px -12px ${currentTheme.colors.primary[500]}`,
                }}
              >
                <Edit3 className="h-4 w-4" />
                Change
              </button>
              <button
                onClick={() => {
                  setSelectedTable(null);
                  setSelectedItems([]);
                  setOrderNotes('');
                  setIsEditingExisting(false);
                  setExistingOrder(null);
                }}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold rounded-2xl border transition-colors hover:bg-[var(--color-primary-50)]"
                style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary, color: currentTheme.colors.text.primary }}
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {!selectedTable ? (
        /* Full Page Table Selection */
        <div className="px-3 sm:px-4 md:px-6 pb-3 sm:pb-4 md:pb-6">
          <div
            className="rounded-2xl border p-5 sm:p-7"
            style={{
              backgroundColor: currentTheme.colors.background.card,
              borderColor: currentTheme.colors.border.primary,
              boxShadow: '0 1px 2px rgba(16,24,40,0.04), 0 12px 30px -16px rgba(16,24,40,0.20)'
            }}
          >
            <div className="flex items-center justify-between mb-5 sm:mb-6">
              <div>
                <h2 className="text-lg sm:text-xl font-bold" style={{ color: currentTheme.colors.text.primary }}>
                  Select a Table
                </h2>
                <p className="text-xs sm:text-sm mt-0.5" style={{ color: currentTheme.colors.text.secondary }}>
                  Choose a table to start placing your order
                </p>
              </div>
              <span
                className="hidden sm:inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
                style={{ backgroundColor: currentTheme.colors.primary[50], color: currentTheme.colors.primary[700] }}
              >
                {tables?.length ?? 0} tables
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
              {tables?.map(table => {
                const isInactive = !table.active;
                const isOccupied = (table.currentOrders?.length ?? 0) > 0;

                let accent = '#16a34a'; // available
                if (isInactive) accent = '#9ca3af';
                else if (isOccupied) accent = '#2563eb';
                else if (table.status === 'RESERVED') accent = '#d97706';
                else if (table.status === 'OUT_OF_SERVICE') accent = '#dc2626';

                const statusLabel = isInactive ? 'Inactive'
                  : isOccupied ? 'Occupied'
                  : table.status === 'RESERVED' ? 'Reserved'
                  : table.status === 'OUT_OF_SERVICE' ? 'Out of Service'
                  : 'Available';

                return (
                  <div
                    key={table.id}
                    onClick={() => handleTableSelect(table)}
                    className={clsx(
                      "group rounded-2xl border overflow-hidden transition-all duration-200",
                      isInactive ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:-translate-y-1"
                    )}
                    style={{
                      backgroundColor: currentTheme.colors.background.card,
                      borderColor: currentTheme.colors.border.primary,
                      boxShadow: '0 1px 2px rgba(16,24,40,0.04), 0 10px 24px -16px rgba(16,24,40,0.25)'
                    }}
                  >
                    {/* status accent strip */}
                    <div className="h-1.5 w-full" style={{ background: `linear-gradient(90deg, ${accent}, ${accent}99)` }} />
                    {/* Card header */}
                    <div className="p-3 sm:p-4">
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-base sm:text-lg font-bold truncate" style={{ color: currentTheme.colors.text.primary }}>{table.name}</span>
                        <span className="h-2.5 w-2.5 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: accent, boxShadow: `0 0 0 3px ${accent}22` }} />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs" style={{ color: currentTheme.colors.text.secondary }}>{table.capacity} seats</span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: `${accent}1A`, color: accent }}>{statusLabel}</span>
                      </div>
                    </div>

                    {/* Orders section */}
                    {isOccupied && (
                      <div className="px-2 pb-2 space-y-1.5" onClick={e => e.stopPropagation()}>
                        {table.currentOrders!.map(order => (
                          <div key={order.id} className="rounded-xl p-2 border" style={{ backgroundColor: currentTheme.colors.background.secondary, borderColor: currentTheme.colors.border.primary }}>
                            <p className="text-[10px] font-semibold mb-1.5 truncate" style={{ color: currentTheme.colors.text.secondary }}>
                              #{order.orderNo} · {order.items.length} items · ₹{order.total.toLocaleString()}
                            </p>
                            <div className="flex gap-1">
                              <button
                                onClick={() => handlePrintKOT(order)}
                                disabled={printingKOT}
                                className="flex-1 py-1.5 text-[10px] font-semibold rounded-lg border flex items-center justify-center gap-1 transition-colors disabled:opacity-50"
                                style={{ backgroundColor: currentTheme.colors.background.card, color: currentTheme.colors.text.secondary, borderColor: currentTheme.colors.border.primary }}
                              >
                                {printingKOT
                                  ? <span className="h-2.5 w-2.5 rounded-full border-2 border-current border-t-transparent animate-spin" />
                                  : <Printer className="h-2.5 w-2.5" />} KOT
                              </button>
                              <button
                                onClick={() => {
                                  setSettleTargetOrder(order);
                                  setMergeSelectedIds([]);
                                  setSettlementData({ paymentMode: 'CASH', customerId: '', customerName: '', customerMobile: '', customerEmail: '', customerAddress: '', discountPercent: null, discountAmount: null });
                                  setDiscountType(null);
                                  setSettleRoundOff(true);
                                  setSelectedCustomerForSettle(null);
                                  setSettleMobileError('');
                                  setSettleEmailError('');
                                  setSettlePrintShare(false);
                                  setShowSettleModal(true);
                                }}
                                className="flex-1 py-1 text-[10px] font-medium rounded border flex items-center justify-center gap-0.5 transition-colors text-white border-green-600 bg-green-600 hover:bg-green-700"
                              >
                                <CheckCircle className="h-2.5 w-2.5" /> Settle
                              </button>
                            </div>
                          </div>
                        ))}
                        {table.currentOrders!.length > 1 && (
                          <button
                            onClick={() => confirmMergeOrders(table.currentOrders!)}
                            disabled={standaloneMergeMutation.isLoading}
                            className="w-full py-1 text-[10px] font-medium rounded border flex items-center justify-center gap-1 transition-colors bg-white text-gray-700 border-gray-300 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {standaloneMergeMutation.isLoading ? (
                              <svg className="animate-spin h-2.5 w-2.5 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 22 6.477 22 12h-4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                            ) : (
                              <><GitMerge className="h-2.5 w-2.5" /> Merge Orders</>
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        /* Menu Items */
        <div className="px-3 sm:px-4 md:px-6 pb-36">
          <div className="flex flex-col min-h-[400px]">
            <div
              className="rounded-2xl border flex-1 flex flex-col"
              style={{
                backgroundColor: currentTheme.colors.background.card,
                borderColor: currentTheme.colors.border.primary,
                boxShadow: '0 1px 2px rgba(16,24,40,0.04), 0 12px 30px -16px rgba(16,24,40,0.20)'
              }}
            >
              {/* Search Header */}
              <div className="p-4 sm:p-6 border-b" style={{ borderColor: currentTheme.colors.border.primary }}>
                <h2 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4" style={{ color: currentTheme.colors.text.primary }}>
                  {isEditingExisting ? 'Update Items' : 'Select Items'}
                </h2>
                <div className="relative">
                  <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-5 w-5" style={{ color: currentTheme.colors.text.secondary }} />
                  <input
                    type="text"
                    placeholder="Search food items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 border-2 rounded-2xl focus:ring-4 focus:border-transparent text-sm sm:text-base transition-shadow"
                    style={{
                      borderColor: currentTheme.colors.border.primary,
                      backgroundColor: currentTheme.colors.background.secondary,
                      color: currentTheme.colors.text.primary,
                      '--tw-ring-color': `${currentTheme.colors.primary[500]}33`
                    } as React.CSSProperties}
                  />
                </div>

                {/* Filter Chips */}
                <div className="mt-3 space-y-2">
                  {/* Veg type chips */}
                  <div className="flex gap-1.5 flex-wrap">
                    {[
                      { label: 'All', value: '' },
                      { label: '🟢 Veg', value: 'VEG' },
                      { label: '🔴 Non-Veg', value: 'NON_VEG' },
                      { label: '🌿 Vegan', value: 'VEGAN' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setSelectedVegType(opt.value)}
                        className="px-2.5 py-1 rounded-full text-xs font-medium border transition-all"
                        style={{
                          backgroundColor: selectedVegType === opt.value ? currentTheme.colors.primary[600] : currentTheme.colors.background.secondary,
                          color: selectedVegType === opt.value ? '#fff' : currentTheme.colors.text.primary,
                          borderColor: selectedVegType === opt.value ? currentTheme.colors.primary[600] : currentTheme.colors.border.primary,
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {/* Category chips - horizontal scroll */}
                  {categories && categories.length > 0 && (
                    <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
                      <button
                        onClick={() => setSelectedCategory('')}
                        className="px-2.5 py-1 rounded-full text-xs font-medium border flex-shrink-0 transition-all"
                        style={{
                          backgroundColor: selectedCategory === '' ? currentTheme.colors.primary[600] : currentTheme.colors.background.secondary,
                          color: selectedCategory === '' ? '#fff' : currentTheme.colors.text.primary,
                          borderColor: selectedCategory === '' ? currentTheme.colors.primary[600] : currentTheme.colors.border.primary,
                        }}
                      >
                        All
                      </button>
                      {categories.map(cat => (
                        <button
                          key={cat.id}
                          onClick={() => setSelectedCategory(cat.id)}
                          className="px-2.5 py-1 rounded-full text-xs font-medium border flex-shrink-0 transition-all"
                          style={{
                            backgroundColor: selectedCategory === cat.id ? currentTheme.colors.primary[600] : currentTheme.colors.background.secondary,
                            color: selectedCategory === cat.id ? '#fff' : currentTheme.colors.text.primary,
                            borderColor: selectedCategory === cat.id ? currentTheme.colors.primary[600] : currentTheme.colors.border.primary,
                          }}
                        >
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {itemsLoading ? (
                  <div className="mt-2">
                    <InlineLoader text={searchTerm ? 'Searching...' : 'Loading items...'} />
                  </div>
                ) : (
                  <p className="text-xs sm:text-sm mt-2" style={{ color: currentTheme.colors.text.secondary }}>
                    {searchTerm ? `Showing results for "${searchTerm}"` : 'Showing all available items'}
                  </p>
                )}
              </div>

              {/* Items List */}
              <div className="flex-1 overflow-y-auto p-3 sm:p-4 pb-28 lg:pb-4">
                {itemsLoading ? (
                  <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {[...Array(6)].map((_, i) => (
                      <SkeletonCard key={i} />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {items?.map((item: Item) => {
                      const addedItem = selectedItems.find(si => si.itemId === item.id);
                      const isAdded = !!addedItem;
                      const kitchen = kitchenMap[item.kitchenId];
                      const isKitchenInactive = kitchen ? !kitchen.active : false;
                      const isUnavailable = !item.available || isKitchenInactive;
                      const imgLen = item.images?.length ?? 0;
                      const currImgIdx = getImgIdx(item.id);
                      const heroImage = imgLen > 0 ? item.images![currImgIdx] : undefined;

                      return (
                        <div
                          key={item.id}
                          onMouseEnter={() => { hoveredItemIdRef.current = item.id; }}
                          onMouseLeave={() => { hoveredItemIdRef.current = null; }}
                          className={`rounded-2xl border overflow-hidden transition-all duration-200 flex flex-col ${!isUnavailable ? 'hover:-translate-y-1' : 'opacity-60'}`}
                          style={{
                            borderColor: isAdded ? currentTheme.colors.primary[400] : currentTheme.colors.border.primary,
                            backgroundColor: currentTheme.colors.background.card,
                            boxShadow: isAdded
                              ? `0 0 0 2px ${currentTheme.colors.primary[200]}, 0 14px 30px -16px ${currentTheme.colors.primary[500]}`
                              : '0 1px 2px rgba(16,24,40,0.04), 0 10px 24px -18px rgba(16,24,40,0.25)',
                          }}
                        >
                          {/* Image section */}
                          <div className="relative w-full h-40 sm:h-48 flex-shrink-0 overflow-hidden bg-gradient-to-br from-orange-50 to-amber-100">
                            {heroImage ? (
                              <img
                                src={heroImage}
                                alt={item.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center gap-1"
                                style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary[50]}, ${currentTheme.colors.primary[100]})` }}>
                                <ChefHat className="h-8 w-8 sm:h-10 sm:w-10" style={{ color: currentTheme.colors.primary[300] }} />
                                <span className="text-[10px] font-medium" style={{ color: currentTheme.colors.primary[400] }}>No photo</span>
                              </div>
                            )}

                            {/* Prev / Next arrows — only if multiple images and available */}
                            {imgLen > 1 && !isUnavailable && (
                              <>
                                <button
                                  onClick={(e) => shiftImg(e, item.id, imgLen, -1)}
                                  className="absolute left-1 top-1/2 -translate-y-1/2 w-5 h-5 bg-black/40 hover:bg-black/60 rounded-full flex items-center justify-center text-white text-xs font-bold z-10 transition-colors"
                                >‹</button>
                                <button
                                  onClick={(e) => shiftImg(e, item.id, imgLen, 1)}
                                  className="absolute right-1 top-1/2 -translate-y-1/2 w-5 h-5 bg-black/40 hover:bg-black/60 rounded-full flex items-center justify-center text-white text-xs font-bold z-10 transition-colors"
                                >›</button>
                              </>
                            )}

                            {/* Veg/Non-veg dot badge */}
                            <div className="absolute top-2 left-2">
                              {item.vegType === 'VEG' || item.vegType === 'VEGAN' ? (
                                <div className="w-5 h-5 bg-white rounded border-2 border-green-500 flex items-center justify-center shadow-sm">
                                  <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                                </div>
                              ) : (
                                <div className="w-5 h-5 bg-white rounded border-2 border-red-500 flex items-center justify-center shadow-sm">
                                  <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                                </div>
                              )}
                            </div>
                            {/* Unavailable overlay */}
                            {isUnavailable && (
                              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                <span className="text-white text-xs font-bold bg-red-600 px-2 py-1 rounded-full shadow">
                                  {isKitchenInactive ? 'Kitchen Closed' : 'Unavailable'}
                                </span>
                              </div>
                            )}
                            {/* Added badge */}
                            {isAdded && !isUnavailable && (
                              <div className="absolute top-2 right-2 flex items-center gap-1 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow">
                                <Check className="h-2.5 w-2.5" />
                                <span>{addedItem!.quantity}</span>
                              </div>
                            )}

                            {/* Dot indicators — overlaid on image bottom */}
                            {imgLen > 1 && (
                              <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-1 py-1.5 z-10"
                                style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.28))' }}>
                                {item.images!.map((_, i) => (
                                  <div
                                    key={i}
                                    className="h-1.5 rounded-full transition-all duration-300"
                                    style={{
                                      width: i === currImgIdx ? '12px' : '6px',
                                      backgroundColor: i === currImgIdx
                                        ? currentTheme.colors.primary[400]
                                        : 'rgba(255,255,255,0.65)',
                                    }}
                                  />
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Content section */}
                          <div className="p-2.5 sm:p-3">
                            {/* Row: text info left, action right */}
                            <div className="flex items-center gap-2">
                              {/* Text */}
                              <div className="flex-1 min-w-0">
                                <h4 className="text-xs sm:text-sm font-bold leading-tight mb-0.5 line-clamp-2"
                                  style={{ color: currentTheme.colors.text.primary }}>
                                  {item.name}
                                </h4>
                                <div className="flex items-center gap-1 mb-1">
                                  <ChefHat className="h-2.5 w-2.5 flex-shrink-0" style={{ color: currentTheme.colors.text.secondary }} />
                                  <p className="text-[10px] sm:text-xs truncate" style={{ color: currentTheme.colors.text.secondary }}>
                                    {kitchen?.name || item.kitchenName || 'Kitchen'}
                                  </p>
                                </div>
                                <p className="text-sm font-bold" style={{ color: currentTheme.colors.primary[600] }}>
                                  ₹{item.price}
                                </p>
                              </div>

                              {/* Action — right side */}
                              {!isUnavailable && (
                                <div className="flex-shrink-0">
                                  {(!isAdded || hasOptions(item)) ? (
                                    <button
                                      onClick={() => addItem(item)}
                                      className="py-1.5 px-3 text-white rounded-xl text-xs font-semibold transition-transform flex items-center gap-1 hover:scale-105 active:scale-95"
                                      style={{
                                        background: `linear-gradient(135deg, ${currentTheme.colors.primary[500]}, ${currentTheme.colors.primary[700]})`,
                                        boxShadow: `0 8px 18px -8px ${currentTheme.colors.primary[500]}`,
                                      }}
                                    >
                                      <Plus className="h-3.5 w-3.5" />
                                      {hasOptions(item) ? 'Customize' : 'Add'}
                                    </button>
                                  ) : addedItem && (
                                    <div className="flex items-center rounded-lg overflow-hidden border"
                                      style={{ borderColor: currentTheme.colors.primary[300] }}>
                                      <button
                                        onClick={() => updateQuantity(addedItem.lineKey, addedItem.quantity - 1)}
                                        className="px-2 py-1.5 flex items-center justify-center transition-colors"
                                        style={{ backgroundColor: addedItem.quantity === 1 ? '#fef2f2' : currentTheme.colors.primary[50], color: addedItem.quantity === 1 ? '#dc2626' : currentTheme.colors.primary[600] }}
                                      >
                                        {addedItem.quantity === 1 ? <Trash2 className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
                                      </button>
                                      <span className="w-6 text-center text-xs font-bold" style={{ color: currentTheme.colors.text.primary }}>
                                        {addedItem.quantity}
                                      </span>
                                      <button
                                        onClick={() => updateQuantity(addedItem.lineKey, addedItem.quantity + 1)}
                                        className="px-2 py-1.5 flex items-center justify-center transition-colors"
                                        style={{ backgroundColor: currentTheme.colors.primary[50], color: currentTheme.colors.primary[600] }}
                                      >
                                        <Plus className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Note input — below row, only when added (simple items only) */}
                            {isAdded && addedItem && !hasOptions(item) && (
                              <input
                                type="text"
                                placeholder="Add note..."
                                value={addedItem.itemNotes || ''}
                                onChange={(e) => updateItemNotes(addedItem.lineKey, e.target.value)}
                                className="w-full mt-2 px-2 py-1 border rounded-lg text-[10px] sm:text-xs"
                                style={{
                                  borderColor: currentTheme.colors.border.primary,
                                  backgroundColor: currentTheme.colors.background.primary,
                                  color: currentTheme.colors.text.primary
                                }}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>


          {/* Floating Cart Button */}
          {selectedItems.length > 0 && (
            <div className="fixed bottom-4 left-4 right-4 flex justify-center z-40">
              <button
                onClick={() => setShowOrderSummaryModal(true)}
                className="w-full max-w-lg py-3.5 text-white rounded-2xl transition-all flex items-center justify-between px-5 shadow-2xl active:scale-[0.98]"
                style={{
                  background: `linear-gradient(135deg, ${currentTheme.colors.primary[600]}, ${currentTheme.colors.primary[700]})`,
                  boxShadow: `0 8px 24px ${currentTheme.colors.primary[600]}55`
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <ShoppingCart className="h-5 w-5" />
                    <span className="absolute -top-2 -right-2 bg-white text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center"
                      style={{ color: currentTheme.colors.primary[600] }}>
                      {selectedItems.length}
                    </span>
                  </div>
                  <span className="font-semibold text-sm">{selectedItems.reduce((s, i) => s + i.quantity, 0)} items added</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-base font-bold">₹{totalAmount.toFixed(0)}</span>
                  <span className="text-xs opacity-80">View Cart →</span>
                </div>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Order Summary Modal */}
      {showOrderSummaryModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 sm:p-6"
          onClick={() => setShowOrderSummaryModal(false)}>
          <div
            className="w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl overflow-hidden flex flex-col"
            style={{ maxHeight: '85vh', backgroundColor: currentTheme.colors.background.card, boxShadow: '0 30px 80px -20px rgba(16,24,40,0.45)' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Grab handle (mobile) */}
            <div className="sm:hidden flex justify-center pt-2.5 pb-1">
              <span className="h-1.5 w-10 rounded-full" style={{ backgroundColor: currentTheme.colors.border.secondary }} />
            </div>
            {/* Header */}
            <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: currentTheme.colors.border.primary }}>
              <h2 className="text-lg font-bold flex items-center gap-2.5" style={{ color: currentTheme.colors.text.primary }}>
                <span className="grid place-items-center h-8 w-8 rounded-xl text-white"
                      style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary[500]}, ${currentTheme.colors.primary[700]})` }}>
                  <ShoppingCart className="h-4 w-4" />
                </span>
                {isEditingExisting ? `Order #${existingOrder?.orderNo}` : `Cart · ${selectedItems.length} items`}
              </h2>
              <button onClick={() => setShowOrderSummaryModal(false)} className="grid place-items-center h-9 w-9 rounded-xl transition-colors hover:bg-[var(--color-primary-50)]" style={{ backgroundColor: currentTheme.colors.background.secondary }}>
                <X className="h-5 w-5" style={{ color: currentTheme.colors.text.primary }} />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {selectedItems.length === 0 ? (
                <div className="text-center py-6">
                  <ShoppingCart className="h-8 w-8 mx-auto mb-2 opacity-30" style={{ color: currentTheme.colors.text.secondary }} />
                  <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                    {isEditingExisting ? 'Add more items from the menu' : 'Select items from the menu'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedItems.map(item => (
                    <div key={item.lineKey} className="p-3 rounded-xl border" style={{ backgroundColor: currentTheme.colors.background.primary, borderColor: currentTheme.colors.border.primary }}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-sm truncate" style={{ color: currentTheme.colors.text.primary }}>
                            {item.name}{item.variantName ? ` · ${item.variantName}` : ''}
                          </h4>
                          {item.modifiers && item.modifiers.length > 0 && (
                            <p className="text-[11px] truncate" style={{ color: currentTheme.colors.text.secondary }}>
                              + {item.modifiers.map(m => m.optionName).join(', ')}
                            </p>
                          )}
                          <p className="text-xs" style={{ color: currentTheme.colors.text.secondary }}>₹{item.unitPrice} each</p>
                        </div>
                        <button onClick={() => setSelectedItems(prev => prev.filter(si => si.lineKey !== item.lineKey))} className="p-1 text-red-400 hover:text-red-600">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center rounded-lg overflow-hidden border" style={{ borderColor: currentTheme.colors.primary[300] }}>
                          <button onClick={() => updateQuantity(item.lineKey, item.quantity - 1)} className="px-3 py-1.5 transition-colors"
                            style={{ backgroundColor: item.quantity === 1 ? '#fef2f2' : currentTheme.colors.primary[50], color: item.quantity === 1 ? '#dc2626' : currentTheme.colors.primary[600] }}>
                            {item.quantity === 1 ? <Trash2 className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
                          </button>
                          <span className="w-8 text-center text-sm font-bold" style={{ color: currentTheme.colors.text.primary }}>{item.quantity}</span>
                          <button onClick={() => updateQuantity(item.lineKey, item.quantity + 1)} className="px-3 py-1.5 transition-colors"
                            style={{ backgroundColor: currentTheme.colors.primary[50], color: currentTheme.colors.primary[600] }}>
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <p className="text-base font-bold" style={{ color: currentTheme.colors.primary[600] }}>₹{(item.unitPrice * item.quantity).toFixed(0)}</p>
                      </div>
                      <input type="text" placeholder="Special note..."
                        value={item.itemNotes || ''} onChange={e => updateItemNotes(item.lineKey, e.target.value)}
                        className="w-full px-3 py-1.5 border rounded-lg text-xs"
                        style={{ borderColor: currentTheme.colors.border.primary, backgroundColor: currentTheme.colors.background.card, color: currentTheme.colors.text.primary }} />
                    </div>
                  ))}
                </div>
              )}

              {/* Order notes */}
              <div className="border-t pt-3" style={{ borderColor: currentTheme.colors.border.primary }}>
                {!showOrderNotes ? (
                  <button onClick={() => setShowOrderNotes(true)} className="text-sm font-medium flex items-center gap-1" style={{ color: currentTheme.colors.primary[600] }}>
                    <Plus className="h-3.5 w-3.5" /> Add special message
                  </button>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>Special Message</label>
                      <button onClick={() => { setShowOrderNotes(false); setOrderNotes(''); }} className="text-gray-400"><X className="h-4 w-4" /></button>
                    </div>
                    <textarea value={orderNotes} onChange={e => setOrderNotes(e.target.value)} rows={2} autoFocus
                      className="w-full p-2 border rounded-lg resize-none text-sm"
                      style={{ borderColor: currentTheme.colors.border.primary, backgroundColor: currentTheme.colors.background.primary, color: currentTheme.colors.text.primary }}
                      placeholder="Any special instructions..." />
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            {selectedItems.length > 0 && (
              <div className="p-4 border-t" style={{ borderColor: currentTheme.colors.border.primary }}>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-base font-bold" style={{ color: currentTheme.colors.text.primary }}>Total:</span>
                  <span className="text-xl font-bold" style={{ color: currentTheme.colors.primary[600] }}>₹{totalAmount.toFixed(0)}</span>
                </div>
                {isEditingExisting ? (
                  <button onClick={() => { updateOrderMutation.mutate(); setShowOrderSummaryModal(false); }}
                    disabled={primaryActionDisabled}
                    className="w-full py-3.5 text-white text-base font-bold rounded-2xl transition-transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:hover:scale-100"
                    style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary[500]}, ${currentTheme.colors.primary[700]})`, boxShadow: `0 14px 30px -12px ${currentTheme.colors.primary[500]}` }}>
                    {updateOrderMutation.isLoading ? <ButtonLoader text="Updating..." /> : 'Update Order'}
                  </button>
                ) : (
                  <div className="flex flex-col gap-3">
                    <PrintShareToggle
                      checked={placePrintShare}
                      onChange={setPlacePrintShare}
                      share={willUseShare()}
                      label={willUseShare() ? 'Share KOT' : 'Print KOT'}
                      theme={currentTheme}
                      className="self-start"
                    />
                    <button
                      onClick={() => { (placePrintShare ? placeOrderAndPrintMutation : placeOrderMutation).mutate(); setShowOrderSummaryModal(false); }}
                      disabled={primaryActionDisabled}
                      className="w-full py-3.5 text-white text-base font-bold rounded-2xl transition-transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:hover:scale-100"
                      style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary[500]}, ${currentTheme.colors.primary[700]})`, boxShadow: `0 14px 30px -12px ${currentTheme.colors.primary[500]}` }}>
                      {(placeOrderMutation.isLoading || placeOrderAndPrintMutation.isLoading) ? <ButtonLoader text="Placing..." /> : `Place Order (₹${totalAmount.toFixed(0)})`}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Item customization modal (variants + add-ons) */}
      {customizing && (
        <CustomizeItemModal
          item={customizing}
          onClose={() => setCustomizing(null)}
          onAdd={(variantName, mods, unitPrice) => { addLine(customizing, variantName, mods, unitPrice); setCustomizing(null); }}
        />
      )}

      {/* Table Change Modal */}
      {showTableModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
          <div
            className="rounded-lg sm:rounded-xl max-w-4xl w-full max-h-[85vh] sm:max-h-[80vh] overflow-hidden"
            style={{ backgroundColor: currentTheme.colors.background.card }}
          >
            <div className="p-4 sm:p-6 border-b flex items-center justify-between" style={{ borderColor: currentTheme.colors.border.primary }}>
              <h2 className="text-base sm:text-xl font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                Select a Different Table
              </h2>
              <button
                onClick={() => setShowTableModal(false)}
                className="p-1.5 sm:p-2 rounded-lg transition-colors"
                style={{ backgroundColor: currentTheme.colors.background.secondary }}
              >
                <X className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </div>

            <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(85vh-80px)] sm:max-h-[60vh]">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                {tables?.map(table => {
                  const status = getTableStatusDisplay(table);
                  const StatusIcon = status.icon;
                  const isCurrentTable = selectedTable?.id === table.id;
                  const isInactive = !table.active;

                  return (
                    <div
                      key={table.id}
                      onClick={() => handleTableSelect(table)}
                      className={clsx(
                        "p-3 sm:p-4 rounded-lg sm:rounded-xl border-2 transition-all",
                        isInactive ? "opacity-50 cursor-not-allowed" : "cursor-pointer transform hover:scale-105"
                      )}
                      style={{
                        backgroundColor: isInactive ? '#fef2f2' : (isCurrentTable ? currentTheme.colors.primary[50] : currentTheme.colors.background.card),
                        borderColor: isInactive ? '#ef4444' : (isCurrentTable ? currentTheme.colors.primary[300] : currentTheme.colors.border.primary)
                      }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-base sm:text-lg font-bold truncate" style={{ color: currentTheme.colors.text.primary }}>
                          {table.name}
                        </span>
                        {isCurrentTable && <Check className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ml-1" style={{ color: currentTheme.colors.primary[600] }} />}
                      </div>

                      <div className="flex items-center justify-between mb-2 gap-2">
                        <span className="text-xs sm:text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                          {table.capacity} seats
                        </span>
                      </div>

                      <div className={clsx('px-2 py-1 rounded-lg text-xs font-semibold w-full text-center border', status.color)}>
                        <div className="flex items-center justify-center space-x-1">
                          <StatusIcon className="h-3 w-3" />
                          <span>{status.text}</span>
                        </div>
                      </div>

                      {(table.currentOrders?.length ?? 0) > 0 && (
                        <div className="text-xs text-orange-600 bg-orange-50 p-2 rounded mt-2 font-medium border border-orange-200">
                          <div className="flex items-center justify-center space-x-1">
                            <Users className="h-3 w-3" />
                            <span>
                              {table.currentOrders!.length === 1
                                ? `1 Active Order (${table.currentOrders![0].items.length} items)`
                                : `${table.currentOrders!.length} Active Orders`}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NEW: Existing-order choice modal */}
      {showExistingOrderChoice && selectedTable && (selectedTable.currentOrders?.length ?? 0) > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3">
          <div
            className="max-w-lg w-full rounded-xl border p-5"
            style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                This table is in use
              </h3>
              <button
                onClick={() => {
                  setShowExistingOrderChoice(false);
                  setSelectedTable(null);
                }}
                className="p-1 rounded-lg transition-colors"
                style={{ backgroundColor: currentTheme.colors.background.secondary }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.hover}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.secondary}
              >
                <X className="h-5 w-5" style={{ color: currentTheme.colors.text.secondary }} />
              </button>
            </div>
            <p className="text-sm mb-4" style={{ color: currentTheme.colors.text.secondary }}>
              Place a new order for this table, or update an existing one.
            </p>

            {/* Primary action — place a new order */}
            <button
              onClick={() => {
                setIsEditingExisting(false);
                setExistingOrder(null);
                setSelectedItems([]); // start fresh
                setOrderNotes('');
                setShowExistingOrderChoice(false);
                setShowTableModal(false);
              }}
              className="w-full py-3.5 rounded-xl text-white text-base font-semibold flex items-center justify-center gap-2 shadow-md transition-colors"
              style={{ backgroundColor: currentTheme.colors.primary[600] }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700]}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600]}
            >
              <Plus className="h-5 w-5" />
              Place a New Order
            </button>

            {/* Existing orders — compact rows, pencil to update */}
            <p className="text-[11px] font-semibold uppercase tracking-wider mt-5 mb-2" style={{ color: currentTheme.colors.text.secondary }}>
              Existing Orders
            </p>
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {selectedTable.currentOrders!.map((ord) => (
                <div
                  key={ord.id}
                  className="border rounded-lg px-3 py-2 flex items-center justify-between"
                  style={{ borderColor: currentTheme.colors.border.primary, backgroundColor: currentTheme.colors.background.secondary }}
                >
                  <div>
                    <div className="text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>
                      {ord.orderNo} • ₹{ord.total.toLocaleString()}
                    </div>
                    <div className="text-xs" style={{ color: currentTheme.colors.text.secondary }}>
                      {ord.items.length} items
                    </div>
                  </div>
                  <button
                    onClick={() => loadExistingOrderForEdit(ord)}
                    title={`Update order ${ord.orderNo}`}
                    className="p-2 rounded-lg transition-colors"
                    style={{ backgroundColor: currentTheme.colors.primary[100] }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[200]}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[100]}
                  >
                    <Edit3 className="h-4 w-4" style={{ color: currentTheme.colors.primary[700] }} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Unplaced-order leave confirmation ───────────────────────────── */}
      {navigationBlocker.state === 'blocked' && selectedTable && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-3">
          <div
            className="max-w-md w-full rounded-xl border p-5"
            style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}
          >
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0" />
              <h3 className="text-lg font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                Order Not Placed
              </h3>
            </div>
            <p className="text-sm mb-5" style={{ color: currentTheme.colors.text.secondary }}>
              You selected table <span className="font-semibold">{selectedTable.name}</span> but the order has not been placed yet.
              Do you want to cancel it and leave, or stay and continue?
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => navigationBlocker.reset()}
                className="flex-1 py-2.5 rounded-lg text-white text-sm font-semibold transition-colors"
                style={{ backgroundColor: currentTheme.colors.primary[600] }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700]}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600]}
              >
                Stay
              </button>
              <button
                onClick={() => {
                  resetOrderSession();
                  navigationBlocker.proceed();
                }}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold border transition-colors text-red-600 border-red-300 hover:bg-red-50"
                style={{ backgroundColor: currentTheme.colors.background.secondary }}
              >
                Cancel Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Post-settle WhatsApp share prompt ───────────────────────────── */}
      <WhatsAppShareModal
        orderId={waShare?.orderId ?? null}
        defaultMobile={waShare?.mobile}
        defaultName={waShare?.name}
        onClose={() => setWaShare(null)}
      />

      {/* ── Settle Order Modal ──────────────────────────────────────────── */}
      {showSettleModal && settleTargetOrder && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-50">
          <div
            className="rounded-2xl sm:rounded-3xl max-w-4xl w-full h-[95vh] sm:h-[90vh] md:h-[85vh] overflow-hidden flex flex-col"
            style={{ backgroundColor: currentTheme.colors.background.card, boxShadow: '0 30px 80px -20px rgba(16,24,40,0.45)' }}
          >
            {/* Fixed Header */}
            <div className="p-4 sm:p-5 md:p-6 border-b flex-shrink-0" style={{ borderColor: currentTheme.colors.border.primary }}>
              <div className="flex items-center justify-between">
                <h2 className="text-lg sm:text-xl font-bold flex items-center gap-2.5" style={{ color: currentTheme.colors.text.primary }}>
                  <span className="grid place-items-center h-9 w-9 rounded-xl text-white"
                        style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary[500]}, ${currentTheme.colors.primary[700]})` }}>
                    <CheckCircle className="h-5 w-5" />
                  </span>
                  Settle Order
                </h2>
                <button
                  onClick={() => setShowSettleModal(false)}
                  className="grid place-items-center h-9 w-9 rounded-xl transition-colors hover:bg-[var(--color-primary-50)]"
                  style={{ backgroundColor: currentTheme.colors.background.secondary }}
                >
                  <X className="h-5 w-5" style={{ color: currentTheme.colors.text.primary }} />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-3 sm:p-4 md:p-6">
                <div className="flex flex-col lg:grid lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8">

                  {/* Left Column - Order Summary & Payment */}
                  <div className="space-y-4 sm:space-y-6">
                    {/* Order Summary */}
                    <div className="rounded-lg p-3 sm:p-4" style={{ backgroundColor: currentTheme.colors.background.secondary }}>
                      <div className="flex justify-between items-center mb-2 gap-2">
                        <span className="font-medium text-sm sm:text-base" style={{ color: currentTheme.colors.text.primary }}>
                          {settleTargetOrder.orderNo}
                        </span>
                        <span className="text-xl sm:text-2xl font-bold" style={{ color: currentTheme.colors.primary[600] }}>
                          ₹{settleTargetOrder.total.toLocaleString()}
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                        Table: {tables.find(t => t.id === settleTargetOrder.tableId)?.name || '—'} • {settleTargetOrder.items.length} items
                      </p>
                      <div className="mt-3 pt-3 border-t" style={{ borderColor: currentTheme.colors.border.primary }}>
                        <div className="flex justify-between text-sm">
                          <span style={{ color: currentTheme.colors.text.secondary }}>Subtotal:</span>
                          <span style={{ color: currentTheme.colors.text.primary }}>₹{settleTargetOrder.subTotal?.toLocaleString()}</span>
                        </div>
                        {isGstApplicable && settleTargetOrder.totalGstAmount && settleTargetOrder.totalGstAmount > 0 && (
                          <>
                            <div className="flex justify-between text-sm">
                              <span style={{ color: currentTheme.colors.text.secondary }}>CGST ({(settleTargetOrder.totalGstAmount / (settleTargetOrder.subTotal || 1) * 50).toFixed(1)}%):</span>
                              <span style={{ color: currentTheme.colors.text.primary }}>₹{(settleTargetOrder.totalGstAmount / 2).toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span style={{ color: currentTheme.colors.text.secondary }}>SGST ({(settleTargetOrder.totalGstAmount / (settleTargetOrder.subTotal || 1) * 50).toFixed(1)}%):</span>
                              <span style={{ color: currentTheme.colors.text.primary }}>₹{(settleTargetOrder.totalGstAmount / 2).toFixed(2)}</span>
                            </div>
                          </>
                        )}
                        <div className="flex justify-between text-sm font-semibold">
                          <span style={{ color: currentTheme.colors.text.primary }}>Total:</span>
                          <span style={{ color: currentTheme.colors.text.primary }}>₹{settleTargetOrder.total.toLocaleString()}</span>
                        </div>
                        {(() => {
                          const base = settleTargetOrder.total;
                          const dPct = (settlementData.discountPercent || 0) > 0 ? (settlementData.discountPercent as number) : 0;
                          const dAmt = (settlementData.discountAmount || 0) > 0 ? (settlementData.discountAmount as number) : 0;
                          const discountVal = dPct > 0 ? (base * dPct) / 100 : dAmt;
                          const payableBeforeRound = Math.max(0, base - discountVal);
                          const payable = settleRoundOff ? Math.floor(payableBeforeRound) : payableBeforeRound;
                          const roundOff = payableBeforeRound - payable;
                          const hasDiscount = discountVal > 0;
                          const hasRound = settleRoundOff && roundOff > 0.0001;
                          if (!hasDiscount && !hasRound) return null;
                          return (
                            <>
                              {dPct > 0 && (
                                <div className="flex justify-between text-sm mt-2 pt-2 border-t" style={{ borderColor: currentTheme.colors.border.primary }}>
                                  <span style={{ color: currentTheme.colors.text.secondary }}>Discount ({dPct}%):</span>
                                  <span className="text-red-600">-₹{((base * dPct) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                                </div>
                              )}
                              {dAmt > 0 && (
                                <div className="flex justify-between text-sm">
                                  <span style={{ color: currentTheme.colors.text.secondary }}>Discount Amount:</span>
                                  <span className="text-red-600">-₹{dAmt.toLocaleString()}</span>
                                </div>
                              )}
                              {hasRound && (
                                <div className="flex justify-between text-sm">
                                  <span style={{ color: currentTheme.colors.text.secondary }}>Round off:</span>
                                  <span className="text-red-600">-₹{roundOff.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                                </div>
                              )}
                              <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2" style={{ borderColor: currentTheme.colors.border.primary }}>
                                <span style={{ color: currentTheme.colors.text.primary }}>Final Total:</span>
                                <span className="text-green-600">₹{payable.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Merge Orders Section */}
                    {(() => {
                      const mergeableOrders = openOrdersList.filter(
                        o => o.tableId === settleTargetOrder.tableId && o.id !== settleTargetOrder.id && o.status === 'OPEN'
                      );
                      if (mergeableOrders.length === 0) return null;
                      return (
                        <div className="p-3 sm:p-4 rounded-lg border-2 border-dashed" style={{ borderColor: currentTheme.colors.primary[300] }}>
                          <h3 className="font-semibold mb-3 flex items-center text-sm sm:text-base" style={{ color: currentTheme.colors.text.primary }}>
                            <GitMerge className="h-4 w-4 sm:h-5 sm:w-5 mr-2" style={{ color: currentTheme.colors.primary[600] }} />
                            Merge Orders (Optional)
                          </h3>
                          <p className="text-xs mb-3" style={{ color: currentTheme.colors.text.secondary }}>
                            Select orders from the same table to merge before settling.
                          </p>
                          <div className="space-y-2 mb-3">
                            {mergeableOrders.map(o => (
                              <label key={o.id} className="flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors" style={{
                                backgroundColor: mergeSelectedIds.includes(o.id) ? currentTheme.colors.primary[50] : currentTheme.colors.background.secondary,
                                border: `1px solid ${mergeSelectedIds.includes(o.id) ? currentTheme.colors.primary[300] : currentTheme.colors.border.primary}`
                              }}>
                                <input
                                  type="checkbox"
                                  checked={mergeSelectedIds.includes(o.id)}
                                  onChange={e => {
                                    if (e.target.checked) setMergeSelectedIds(prev => [...prev, o.id]);
                                    else setMergeSelectedIds(prev => prev.filter(id => id !== o.id));
                                  }}
                                  className="w-4 h-4 rounded"
                                />
                                <div className="flex-1 min-w-0">
                                  <span className="text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>#{o.orderNo}</span>
                                  <span className="text-xs ml-2" style={{ color: currentTheme.colors.text.secondary }}>{o.items.length} items</span>
                                </div>
                                <span className="text-sm font-semibold" style={{ color: currentTheme.colors.primary[600] }}>₹{o.total.toLocaleString()}</span>
                              </label>
                            ))}
                          </div>
                          {mergeSelectedIds.length > 0 && (
                            <button
                              onClick={() => mergeOrdersMutation.mutate({ masterOrderId: settleTargetOrder.id, sourceOrderIds: mergeSelectedIds })}
                              disabled={mergeOrdersMutation.isLoading}
                              className="w-full py-2 rounded-lg text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                              style={{ backgroundColor: currentTheme.colors.primary[600] }}
                            >
                              {mergeOrdersMutation.isLoading ? (
                                <ButtonLoader text="Merging..." />
                              ) : (
                                <><GitMerge className="h-4 w-4" />Merge {mergeSelectedIds.length} Order{mergeSelectedIds.length > 1 ? 's' : ''}</>
                              )}
                            </button>
                          )}
                        </div>
                      );
                    })()}

                    {/* Discount Section */}
                    <div className="p-3 sm:p-4 rounded-lg border-2 border-dashed" style={{ borderColor: currentTheme.colors.border.primary }}>
                      <h3 className="font-semibold mb-3 sm:mb-4 flex items-center text-sm sm:text-base" style={{ color: currentTheme.colors.text.primary }}>
                        <Percent className="h-4 w-4 sm:h-5 sm:w-5 mr-2" style={{ color: currentTheme.colors.primary[600] }} />
                        Apply Discount (Optional)
                      </h3>
                      <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3 sm:mb-4">
                        <button
                          onClick={() => { setDiscountType('percent'); setSettlementData(prev => ({ ...prev, discountAmount: null })); }}
                          className="p-2 sm:p-3 rounded-lg border-2 transition-all flex items-center justify-center space-x-1 sm:space-x-2"
                          style={{
                            borderColor: discountType === 'percent' ? currentTheme.colors.primary[500] : currentTheme.colors.border.primary,
                            backgroundColor: discountType === 'percent' ? currentTheme.colors.primary[50] : currentTheme.colors.background.secondary,
                            color: discountType === 'percent' ? currentTheme.colors.primary[700] : currentTheme.colors.text.primary
                          }}
                        >
                          <Percent className="h-3 w-3 sm:h-4 sm:w-4" />
                          <span className="font-medium text-xs sm:text-sm">Discount %</span>
                        </button>
                        <button
                          onClick={() => { setDiscountType('amount'); setSettlementData(prev => ({ ...prev, discountPercent: null })); }}
                          className="p-2 sm:p-3 rounded-lg border-2 transition-all flex items-center justify-center space-x-1 sm:space-x-2"
                          style={{
                            borderColor: discountType === 'amount' ? currentTheme.colors.primary[500] : currentTheme.colors.border.primary,
                            backgroundColor: discountType === 'amount' ? currentTheme.colors.primary[50] : currentTheme.colors.background.secondary,
                            color: discountType === 'amount' ? currentTheme.colors.primary[700] : currentTheme.colors.text.primary
                          }}
                        >
                          <IndianRupee className="h-3 w-3 sm:h-4 sm:w-4" />
                          <span className="font-medium text-xs sm:text-sm">Discount ₹</span>
                        </button>
                      </div>
                      {discountType && (
                        <div>
                          <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                            {discountType === 'percent' ? 'Discount Percentage' : 'Discount Amount (₹)'}
                          </label>
                          <input
                            type="number"
                            value={discountType === 'percent' ? (settlementData.discountPercent || '') : (settlementData.discountAmount || '')}
                            onChange={(e) => {
                              const value = e.target.value ? parseFloat(e.target.value) : null;
                              if (discountType === 'percent') setSettlementData(prev => ({ ...prev, discountPercent: value }));
                              else setSettlementData(prev => ({ ...prev, discountAmount: value }));
                            }}
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg text-sm"
                            style={{ borderColor: currentTheme.colors.border.primary, backgroundColor: currentTheme.colors.background.secondary, color: currentTheme.colors.text.primary }}
                            placeholder={discountType === 'percent' ? 'Enter percentage (0-100)' : 'Enter amount'}
                            min="0"
                            max={discountType === 'percent' ? "100" : undefined}
                          />
                        </div>
                      )}

                      {/* Round off to whole ₹ */}
                      <label className="flex items-center gap-2 mt-3 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={settleRoundOff}
                          onChange={(e) => setSettleRoundOff(e.target.checked)}
                          className="h-4 w-4 rounded"
                          style={{ accentColor: currentTheme.colors.primary[600] }}
                        />
                        <span className="text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>Round off to whole ₹</span>
                      </label>
                    </div>

                    {/* Payment Mode */}
                    <div>
                      <h3 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base" style={{ color: currentTheme.colors.text.primary }}>Payment Method</h3>
                      <div className="grid grid-cols-2 gap-2 sm:gap-3">
                        {(['CASH', 'UPI', 'CARD', 'DUE'] as PaymentMode[]).map((mode) => (
                          <button
                            key={mode}
                            onClick={() => setSettlementData(prev => ({ ...prev, paymentMode: mode }))}
                            className="p-2.5 sm:p-3 md:p-4 rounded-lg border-2 transition-all flex items-center justify-center space-x-1 sm:space-x-2"
                            style={{
                              borderColor: settlementData.paymentMode === mode ? currentTheme.colors.primary[500] : currentTheme.colors.border.primary,
                              backgroundColor: settlementData.paymentMode === mode ? currentTheme.colors.primary[50] : currentTheme.colors.background.secondary,
                              color: settlementData.paymentMode === mode ? currentTheme.colors.primary[700] : currentTheme.colors.text.primary
                            }}
                          >
                            {mode === 'CASH' && <Banknote className="h-4 w-4 sm:h-5 sm:w-5" />}
                            {mode === 'UPI' && <CreditCard className="h-4 w-4 sm:h-5 sm:w-5" />}
                            {mode === 'CARD' && <CreditCard className="h-4 w-4 sm:h-5 sm:w-5" />}
                            {mode === 'DUE' && <Calendar className="h-4 w-4 sm:h-5 sm:w-5" />}
                            <span className="font-medium text-xs sm:text-sm">{mode}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Customer Information */}
                  <div className="mt-6 lg:mt-0">
                    <div className="mb-4 sm:mb-6">
                      <h3 className="font-semibold mb-3 sm:mb-4 flex flex-wrap items-center gap-2 text-sm sm:text-base" style={{ color: currentTheme.colors.text.primary }}>
                        <Users className="h-4 w-4 sm:h-5 sm:w-5 mr-1" style={{ color: currentTheme.colors.primary[600] }} />
                        Customer Information
                        {settlementData.paymentMode === 'DUE' && (
                          <span className="text-[10px] sm:text-xs px-2 py-0.5 sm:py-1 bg-red-100 text-red-700 rounded-full">
                            Required for DUE
                          </span>
                        )}
                      </h3>

                      <div className="space-y-3 sm:space-y-4">
                        {/* Mobile Number with Search */}
                        <div className="relative">
                          <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                            Mobile Number
                          </label>
                          <div className="relative">
                            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5" style={{ color: currentTheme.colors.text.secondary }} />
                            <input
                              type="tel"
                              value={settlementData.customerMobile}
                              onChange={(e) => handleSettleMobileChange(e.target.value)}
                              className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                              style={{
                                borderColor: settleMobileError ? '#ef4444' : (selectedCustomerForSettle ? currentTheme.colors.primary[300] : currentTheme.colors.border.primary),
                                backgroundColor: currentTheme.colors.background.secondary,
                                color: currentTheme.colors.text.primary
                              }}
                              placeholder="Enter 10-digit mobile number"
                              maxLength={10}
                            />
                            {selectedCustomerForSettle && (
                              <button
                                onClick={handleClearSettleCustomerSelection}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-200 rounded"
                                style={{ color: currentTheme.colors.text.secondary }}
                                title="Clear selection and enter manually"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                          {settleMobileError && <p className="text-red-500 text-xs mt-1">{settleMobileError}</p>}

                          {/* Customer Search Dropdown */}
                          {showSettleCustomerDropdown && settleCustomerResults && (settleCustomerResults as any[]).length > 0 && (
                            <div className="absolute top-full left-0 right-0 mt-1 border rounded-lg shadow-lg z-10 max-h-40 overflow-y-auto" style={{
                              backgroundColor: currentTheme.colors.background.card,
                              borderColor: currentTheme.colors.border.primary
                            }}>
                              {(settleCustomerResults as any[]).map((customer: any) => (
                                <div
                                  key={customer.id}
                                  onClick={() => handleSettleCustomerSelect(customer)}
                                  className="p-3 cursor-pointer border-b hover:bg-gray-50 last:border-b-0"
                                  style={{ borderColor: currentTheme.colors.border.secondary }}
                                >
                                  <div className="font-medium" style={{ color: currentTheme.colors.text.primary }}>{customer.name}</div>
                                  <div className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                                    {customer.mobile} {customer.email && `• ${customer.email}`}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Customer Name */}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                            Customer Name
                          </label>
                          <input
                            type="text"
                            value={settlementData.customerName}
                            onChange={(e) => setSettlementData(prev => ({ ...prev, customerName: e.target.value }))}
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                            style={{
                              borderColor: selectedCustomerForSettle ? currentTheme.colors.primary[300] : currentTheme.colors.border.primary,
                              backgroundColor: currentTheme.colors.background.secondary,
                              color: currentTheme.colors.text.primary
                            }}
                            placeholder="Enter customer name"
                            disabled={!!selectedCustomerForSettle}
                          />
                        </div>

                        {/* Email Address */}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                            Email Address <span className="text-xs text-gray-500">(Optional)</span>
                          </label>
                          <input
                            type="email"
                            value={settlementData.customerEmail}
                            onChange={(e) => {
                              setSettlementData(prev => ({ ...prev, customerEmail: e.target.value }));
                              setSettleEmailError(validateEmail(e.target.value));
                            }}
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                            style={{
                              borderColor: settleEmailError ? '#ef4444' : selectedCustomerForSettle ? currentTheme.colors.primary[300] : currentTheme.colors.border.primary,
                              backgroundColor: currentTheme.colors.background.secondary,
                              color: currentTheme.colors.text.primary
                            }}
                            placeholder="Enter email address"
                            disabled={!!selectedCustomerForSettle}
                          />
                          {settleEmailError && <p className="text-xs mt-1" style={{ color: '#ef4444' }}>{settleEmailError}</p>}
                        </div>

                        {/* Address */}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                            Address <span className="text-xs text-gray-500">(Optional)</span>
                          </label>
                          <textarea
                            value={settlementData.customerAddress}
                            onChange={(e) => setSettlementData(prev => ({ ...prev, customerAddress: e.target.value }))}
                            rows={2}
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 resize-none text-sm"
                            style={{
                              borderColor: selectedCustomerForSettle ? currentTheme.colors.primary[300] : currentTheme.colors.border.primary,
                              backgroundColor: currentTheme.colors.background.secondary,
                              color: currentTheme.colors.text.primary
                            }}
                            placeholder="Enter customer address"
                            disabled={!!selectedCustomerForSettle}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Fixed Footer */}
            <div className="p-3 sm:p-4 md:p-6 border-t flex-shrink-0" style={{ borderColor: currentTheme.colors.border.primary }}>
              <div className="flex flex-wrap items-center justify-end gap-3">
                <button
                  onClick={() => setShowSettleModal(false)}
                  className="px-4 py-2.5 mr-auto font-semibold rounded-xl border transition-colors hover:bg-[var(--color-primary-50)]"
                  style={{ backgroundColor: currentTheme.colors.background.secondary, borderColor: currentTheme.colors.border.primary, color: currentTheme.colors.text.primary }}
                >
                  Cancel
                </button>
                <PrintShareToggle
                  checked={settlePrintShare}
                  onChange={setSettlePrintShare}
                  share={willUseShare()}
                  label={willUseShare() ? 'Share bill' : 'Print bill'}
                  theme={currentTheme}
                />
                <button
                  onClick={() => confirmSettlement(settlePrintShare)}
                  disabled={settleOrderMutation.isLoading || settleAndPrintMutation.isLoading}
                  className="px-6 py-2.5 text-white font-bold rounded-xl transition-transform hover:scale-[1.03] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center"
                  style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary[500]}, ${currentTheme.colors.primary[700]})`, boxShadow: `0 12px 26px -12px ${currentTheme.colors.primary[500]}` }}
                >
                  {(settleOrderMutation.isLoading || settleAndPrintMutation.isLoading) ? (
                    <ButtonLoader text="Settling..." />
                  ) : (
                    <><CheckCircle className="h-4 w-4 mr-2" />Settle Order</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}



    </div>
  );
};

// ---------------------------------------------------------------------------
// Item customization modal — pick a variant and add-ons, then add to the cart
// ---------------------------------------------------------------------------
const CustomizeItemModal: React.FC<{
  item: Item;
  onClose: () => void;
  onAdd: (variantName: string | undefined, modifiers: { groupName: string; optionName: string; price: number }[], unitPrice: number) => void;
}> = ({ item, onClose, onAdd }) => {
  const { currentTheme } = useTheme();
  const variants = item.variants || [];
  const groups = item.modifierGroups || [];
  const [variantIdx, setVariantIdx] = useState<number>(variants.length > 0 ? 0 : -1);
  const [selected, setSelected] = useState<Record<string, string[]>>({});

  const base = variantIdx >= 0 && variants[variantIdx] ? variants[variantIdx].price : item.price;

  const chosenModifiers = groups.flatMap(g =>
    (selected[g.name] || []).map(optName => {
      const opt = g.options.find(o => o.name === optName);
      return { groupName: g.name, optionName: optName, price: opt ? opt.price : 0 };
    })
  );
  const unitPrice = base + chosenModifiers.reduce((s, m) => s + m.price, 0);

  const toggleOption = (g: ModifierGroupLite, optName: string) => {
    setSelected(prev => {
      const cur = prev[g.name] || [];
      const single = g.maxSelect === 1;
      let next: string[];
      if (cur.includes(optName)) {
        next = cur.filter(o => o !== optName);
      } else if (single) {
        next = [optName];
      } else if (g.maxSelect && g.maxSelect > 0 && cur.length >= g.maxSelect) {
        return prev; // at max — ignore
      } else {
        next = [...cur, optName];
      }
      return { ...prev, [g.name]: next };
    });
  };

  // Required groups must meet their minimum (default 1 when required)
  const canAdd = groups.every(g => {
    const count = (selected[g.name] || []).length;
    const min = g.required ? Math.max(1, g.minSelect || 0) : (g.minSelect || 0);
    return count >= min;
  });

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
      <div className="rounded-3xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col" style={{ backgroundColor: currentTheme.colors.background.card, boxShadow: '0 30px 80px -20px rgba(16,24,40,0.45)' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: currentTheme.colors.border.primary }}>
          <h2 className="text-lg font-bold truncate pr-3" style={{ color: currentTheme.colors.text.primary }}>{item.name}</h2>
          <button onClick={onClose} className="grid place-items-center h-9 w-9 rounded-xl shrink-0 transition-colors hover:bg-[var(--color-primary-50)]" style={{ backgroundColor: currentTheme.colors.background.secondary, color: currentTheme.colors.text.primary }}><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {variants.length > 0 && (
            <div>
              <p className="text-sm font-semibold mb-2" style={{ color: currentTheme.colors.text.primary }}>Choose size</p>
              <div className="space-y-2">
                {variants.map((v, i) => (
                  <button key={i} onClick={() => setVariantIdx(i)}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg border text-sm"
                    style={{
                      borderColor: variantIdx === i ? currentTheme.colors.primary[500] : currentTheme.colors.border.primary,
                      backgroundColor: variantIdx === i ? currentTheme.colors.primary[50] : 'transparent',
                      color: currentTheme.colors.text.primary,
                    }}>
                    <span>{v.name}</span>
                    <span className="font-semibold" style={{ color: currentTheme.colors.primary[600] }}>₹{v.price}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {groups.map((g, gi) => (
            <div key={gi}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                  {g.name}{g.required && <span className="text-red-500"> *</span>}
                </p>
                <span className="text-xs" style={{ color: currentTheme.colors.text.secondary }}>
                  {g.maxSelect === 1 ? 'choose 1' : g.maxSelect && g.maxSelect > 0 ? `up to ${g.maxSelect}` : 'choose any'}
                </span>
              </div>
              <div className="space-y-2">
                {g.options.map((o, oi) => {
                  const checked = (selected[g.name] || []).includes(o.name);
                  return (
                    <button key={oi} onClick={() => toggleOption(g, o.name)}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-lg border text-sm"
                      style={{
                        borderColor: checked ? currentTheme.colors.primary[500] : currentTheme.colors.border.primary,
                        backgroundColor: checked ? currentTheme.colors.primary[50] : 'transparent',
                        color: currentTheme.colors.text.primary,
                      }}>
                      <span className="flex items-center gap-2">
                        <span className="inline-flex h-4 w-4 items-center justify-center rounded border text-[10px]"
                          style={{ borderColor: checked ? currentTheme.colors.primary[600] : currentTheme.colors.border.secondary, backgroundColor: checked ? currentTheme.colors.primary[600] : 'transparent', color: '#fff' }}>
                          {checked ? '✓' : ''}
                        </span>
                        {o.name}
                      </span>
                      {o.price > 0 && <span style={{ color: currentTheme.colors.text.secondary }}>+₹{o.price}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 border-t" style={{ borderColor: currentTheme.colors.border.primary }}>
          <button onClick={() => onAdd(variantIdx >= 0 ? variants[variantIdx].name : undefined, chosenModifiers, unitPrice)}
            disabled={!canAdd}
            className="w-full py-3.5 text-white text-base font-bold rounded-2xl transition-transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:hover:scale-100"
            style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary[500]}, ${currentTheme.colors.primary[700]})`, boxShadow: `0 14px 30px -12px ${currentTheme.colors.primary[500]}` }}>
            Add to order · ₹{unitPrice.toFixed(0)}
          </button>
          {!canAdd && <p className="text-xs text-red-500 mt-2 text-center">Please complete the required selections.</p>}
        </div>
      </div>
    </div>
  );
};

// Minimal shape used by the customize modal (matches the ModifierGroup type)
type ModifierGroupLite = { name: string; required: boolean; minSelect?: number; maxSelect?: number; options: { name: string; price: number }[] };
